import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Copy, MousePointerClick } from 'lucide-react';
import type { GridCell, SheetLayout } from '@arcanum-hq/shared-types';
import type { ApiError, ModelEditResponse, ModelViewResponse } from '@/lib/api';
import { EmptyState } from '@/components/ui';
import { useModelEdit } from '@/hooks/use-model-edit';
import { formatCellValue } from './cell-style';
import { buildCellIndex, colToA1 } from './grid-helpers';
import {
  buildDependencyTreeData,
  countUpstreamInputs,
  findInputDefinition,
  findOutputDefinition,
  formatProvenanceTimestamp,
  getDownstreamNamedRanges,
  type DependencyNode,
  type DependencyTreeData,
  type ResolvedInputDef,
} from './cell-detail-helpers';
import { EditSyncedToast } from './EditSyncedToast';
import { InputCellEditor } from './InputCellEditor';
import type { LocationResolvedValue } from '@/components/builder/LocationInput';

export interface CellDetailPanelProps {
  selectedCell: { row: number; col: number } | null;
  sheet: SheetLayout;
  schema: ModelViewResponse['schema'];
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  /** Provenance map keyed by namedRange. Each entry conforms to `provenanceRecordSchema` (`{ inputName, value, source, confidence, timestamp, ... }`); web narrows defensively per-render. */
  provenance: Record<string, unknown>;
  /**
   * Click-upstream callback. Phase 4 Session 2 wires upstream clicks to a
   * named-range lookup at the parent level (`<ModelLayout>` resolves the
   * range to `{sheet, row, col}` via `data.schema.allNamedRanges` and
   * dispatches the existing `setSelectedCell` + `setScrollToCell` plumbing
   * shipped in Session 1).
   */
  onUpstreamSelect: (namedRange: string) => void;
  /** Active conversation id — required for input-cell edit submissions; optional for formula/output viewing. */
  conversationId?: string | null;
  /** Phase 4 Session 3: callback for the View-in-Conversation toast link. Parent (Builder route) flips `?tab=conversation`. */
  onViewInConversation?: () => void;
  /**
   * Sandbox edit pathway — `external-sharing` Phase 5 Session 5.4
   * (2026-05-09). When set, input-cell submissions fire this callback
   * INSTEAD of `useModelEdit` (which is the auth-mode commit path
   * keyed by `conversationId`). The shared-deal flow uses this to
   * route edits through `useSandboxOverrides.applyEdit`, which
   * accumulates them client-side and POSTs the override map to
   * `POST /api/shared/:shareId/sandbox-recompute` after a 250ms
   * debounce. The two paths are mutually exclusive: if `onInputEdit`
   * is set, the auth-mode mutation is skipped (no DB write fires); if
   * `conversationId` is set without `onInputEdit`, the auth-mode path
   * runs unchanged.
   */
  onInputEdit?: (namedRange: string, value: unknown) => void;
  /**
   * Billing-driven read-only gate. When true, the input branch's editor is
   * fully disabled (input + Save + Cancel) and the auth-mode `handleSubmit`
   * short-circuits so a programmatic submit can't bypass the disabled
   * controls. Threaded from `<ModelLayout readOnly>` which derives from
   * `useBillingReadOnly()` at 100% usage. signup-billing-mvp post-launch
   * fix 2026-05-14.
   */
  readOnly?: boolean;
}

/**
 * `<CellDetailPanel>` — right-aside detail surface for the Model tab.
 * Width 320px (matches the placeholder it replaces).
 *
 * Phase 4 Session 2 ships the formula/output branch:
 *   - Header: displayName + formatted current value
 *   - Readable formula line + raw-formula toggle + Copy button (with
 *     transient `Copied` confirmation)
 *   - `<ClickableDependencyTree>` — sibling component re-implementing the
 *     `<DependencyTree>` (tool-result) renderer with click affordance on
 *     upstream + downstream nodes. The original tool-result component
 *     stays UNMODIFIED per the design directive.
 *   - Provenance footer: `Derived (calculated from N inputs)`
 *
 * Other branches (Session 2 scope):
 *   - `cellType === 'input'` → "Edit flow ships in Phase 4 Session 3"
 *     placeholder (single short message in the EmptyState style)
 *   - `cellType ∈ {label, empty}` → informational fallback
 *   - No cell selected → "Select a cell to see its details" empty state
 */
export function CellDetailPanel({
  selectedCell,
  sheet,
  schema,
  cellValues,
  inputValues,
  provenance,
  onUpstreamSelect,
  conversationId,
  onViewInConversation,
  onInputEdit,
  readOnly = false,
}: CellDetailPanelProps) {
  const cellIndex = useMemo(() => buildCellIndex(sheet), [sheet]);
  const cell: GridCell | null = selectedCell
    ? cellIndex.get(`${selectedCell.row}-${selectedCell.col}`) ?? null
    : null;

  return (
    <aside
      className="w-80 shrink-0 border-l border-panel-border bg-panel-bg flex flex-col"
      aria-label="Cell detail"
      data-testid="cell-detail-panel"
    >
      <div className="px-3 py-2 border-b border-panel-border">
        <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px]">
          CELL DETAIL
        </span>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="cell-detail-body">
        <PanelBody
          cell={cell}
          sheet={sheet}
          schema={schema}
          cellValues={cellValues}
          inputValues={inputValues}
          provenance={provenance}
          onUpstreamSelect={onUpstreamSelect}
          conversationId={conversationId}
          onViewInConversation={onViewInConversation}
          onInputEdit={onInputEdit}
          readOnly={readOnly}
        />
      </div>
    </aside>
  );
}

interface PanelBodyProps {
  cell: GridCell | null;
  sheet: SheetLayout;
  schema: ModelViewResponse['schema'];
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  provenance: Record<string, unknown>;
  onUpstreamSelect: (namedRange: string) => void;
  conversationId?: string | null;
  onViewInConversation?: () => void;
  /** Sandbox edit pathway — see `CellDetailPanelProps.onInputEdit`. */
  onInputEdit?: (namedRange: string, value: unknown) => void;
  /** Billing read-only gate forwarded to `<InputBranch>`. */
  readOnly?: boolean;
}

function PanelBody({
  cell,
  sheet,
  schema,
  cellValues,
  inputValues,
  provenance,
  onUpstreamSelect,
  conversationId = null,
  onViewInConversation,
  onInputEdit,
  readOnly = false,
}: PanelBodyProps) {
  if (cell === null) {
    return (
      <EmptyState
        icon={<MousePointerClick size={20} />}
        title="No cell selected"
        description="Click a cell in the grid to see its details."
      />
    );
  }

  if (cell.cellType === 'formula' || cell.cellType === 'output') {
    return (
      <FormulaBranch
        cell={cell}
        sheet={sheet}
        schema={schema}
        cellValues={cellValues}
        inputValues={inputValues}
        onUpstreamSelect={onUpstreamSelect}
      />
    );
  }

  if (cell.cellType === 'input') {
    return (
      <InputBranch
        cell={cell}
        schema={schema}
        inputValues={inputValues}
        provenance={provenance}
        conversationId={conversationId}
        onViewInConversation={onViewInConversation}
        onInputEdit={onInputEdit}
        readOnly={readOnly}
      />
    );
  }

  // label / empty
  return (
    <EmptyState
      icon={<MousePointerClick size={20} />}
      title="Informational cell"
      description="This cell holds a label or is empty. Click a formula, output, or input cell to see its details."
    />
  );
}

interface FormulaBranchProps {
  cell: GridCell;
  sheet: SheetLayout;
  schema: ModelViewResponse['schema'];
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  onUpstreamSelect: (namedRange: string) => void;
}

function FormulaBranch({
  cell,
  sheet,
  schema,
  cellValues,
  inputValues,
  onUpstreamSelect,
}: FormulaBranchProps) {
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);

  // Resolve the cell's namedRange (formula cells often DON'T have one — fall
  // back to the A1 cell address as a label so the panel remains useful).
  const namedRange = cell.namedRange ?? null;
  const def = namedRange ? findOutputDefinition(namedRange, schema) : null;
  const headerLabel =
    def?.displayName ?? namedRange ?? `${sheet.name} ${cell.row},${cell.col}`;

  // Formatted current value via the shared `formatCellValue`. Resolve the
  // raw value off `cellValues` using the canonical `${sheet}!${A1}` key
  // (mirrors `<ModelGrid>`).
  const formattedValue = useMemo(() => {
    const a1 = colToA1(cell.col) + cell.row;
    const raw = cellValues[`${sheet.name}!${a1}`] ?? null;
    return formatCellValue(raw, cell, schema);
  }, [cell, sheet.name, cellValues, schema]);

  const description = cell.formulaDescription ?? null;
  const rawFormula = cell.formula ?? null;
  const showToggle = description !== null && rawFormula !== null;

  const tree: DependencyTreeData | null = useMemo(() => {
    if (!namedRange) return null;
    return buildDependencyTreeData(
      namedRange,
      schema,
      cellValues,
      inputValues,
      sheet.name
    );
  }, [namedRange, schema, cellValues, inputValues, sheet.name]);

  const upstreamInputCount = tree ? countUpstreamInputs(tree) : 0;

  const onCopy = async () => {
    if (!rawFormula) return;
    try {
      await navigator.clipboard.writeText(rawFormula);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write blocked — surface a non-toast inline confirmation
      // so the user knows the action didn't take effect. No global toast.
      setCopied(false);
    }
  };

  return (
    <div className="p-3 space-y-3" data-testid="cell-detail-formula-branch">
      {/* Header */}
      <div>
        <div className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase">
          {cell.cellType === 'formula' ? 'Formula' : 'Output'}
        </div>
        <div
          className="text-sm font-mono text-text-bright mt-0.5"
          data-testid="cell-detail-header"
        >
          {headerLabel}
        </div>
        <div
          className="text-base font-mono text-accent tabular-nums mt-1"
          data-testid="cell-detail-value"
        >
          {formattedValue || '—'}
        </div>
      </div>

      {/* Formula */}
      {(description || rawFormula) && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-mono text-text-dim uppercase tracking-[1px]">
              {showRaw || !description ? 'Raw formula' : 'Formula'}
            </span>
            <div className="flex items-center gap-1">
              {showToggle && (
                <button
                  type="button"
                  onClick={() => setShowRaw((s) => !s)}
                  className="text-[10px] font-mono text-text-muted hover:text-text-bright cursor-pointer bg-transparent border-none px-1.5 py-0.5"
                  data-testid="cell-detail-toggle-raw"
                >
                  {showRaw ? 'Show readable' : 'Show raw'}
                </button>
              )}
              {rawFormula && (
                <button
                  type="button"
                  onClick={onCopy}
                  className="flex items-center gap-1 text-[10px] font-mono text-text-muted hover:text-text-bright cursor-pointer bg-transparent border-none px-1.5 py-0.5"
                  data-testid="cell-detail-copy"
                  aria-label="Copy raw formula"
                >
                  <Copy size={10} />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>
          </div>
          <div
            className="text-xs font-mono text-text bg-bg border border-panel-border rounded-[2px] px-2.5 py-2 break-words whitespace-pre-wrap"
            data-testid="cell-detail-formula-body"
          >
            {showRaw || !description ? rawFormula : description}
          </div>
        </div>
      )}

      {/* Dependency tree */}
      {tree && (tree.upstream.length > 0 || tree.downstream.length > 0) && (
        <ClickableDependencyTree tree={tree} onSelect={onUpstreamSelect} />
      )}

      {/* Provenance footer */}
      {tree && upstreamInputCount > 0 && (
        <div
          className="border-t border-panel-border pt-2 text-xs font-mono text-text-dim"
          data-testid="cell-detail-provenance"
        >
          Derived (calculated from {upstreamInputCount} input
          {upstreamInputCount === 1 ? '' : 's'})
        </div>
      )}
    </div>
  );
}

interface ClickableDependencyTreeProps {
  tree: DependencyTreeData;
  /** Fires when a node label is clicked — receives the node's `name` (a namedRange). */
  onSelect: (namedRange: string) => void;
}

/**
 * Sibling renderer to `<DependencyTree>` (in `tool-results/`) — same visual
 * treatment, but each upstream / downstream node label is a clickable
 * `<button>` that fires `onSelect(node.name)` so the user can walk the
 * dependency chain. The original tool-result component stays UNMODIFIED
 * (verified by the operations-checklist git-diff guard).
 */
function ClickableDependencyTree({
  tree,
  onSelect,
}: ClickableDependencyTreeProps) {
  return (
    <div className="space-y-2.5" data-testid="clickable-dependency-tree">
      {tree.upstream.length > 0 && (
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px] mb-1">
            Upstream
          </div>
          <div className="space-y-0.5">
            {tree.upstream.map((node) => (
              <DepRow
                key={`up-${node.name}`}
                node={node}
                onSelect={onSelect}
                kind="upstream"
              />
            ))}
          </div>
        </div>
      )}
      {tree.downstream.length > 0 && (
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px] mb-1">
            Downstream
          </div>
          <div className="space-y-0.5">
            {tree.downstream.map((node) => (
              <DepRow
                key={`down-${node.name}`}
                node={node}
                onSelect={onSelect}
                kind="downstream"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DepRow({
  node,
  onSelect,
  kind,
}: {
  node: DependencyNode;
  onSelect: (namedRange: string) => void;
  kind: 'upstream' | 'downstream';
}) {
  const dotClass = clsx(
    'w-1 h-1 rounded-full shrink-0',
    kind === 'upstream' && node.isInput && 'bg-blue',
    kind === 'upstream' && !node.isInput && 'bg-text-dim',
    kind === 'downstream' && node.isOutput && 'bg-accent',
    kind === 'downstream' && !node.isOutput && 'bg-text-dim'
  );
  const labelClass = clsx(
    'truncate text-left bg-transparent border-none cursor-pointer p-0',
    kind === 'upstream' && node.isInput && 'text-blue hover:text-text-bright',
    kind === 'upstream' &&
      !node.isInput &&
      'text-text-muted hover:text-text-bright',
    kind === 'downstream' &&
      node.isOutput &&
      'text-accent hover:text-text-bright',
    kind === 'downstream' &&
      !node.isOutput &&
      'text-text-muted hover:text-text-bright'
  );
  return (
    <div
      className="flex items-center justify-between gap-2 text-xs font-mono"
      style={{ paddingLeft: `${node.depth * 12}px` }}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        <span className={dotClass} />
        <button
          type="button"
          onClick={() => onSelect(node.name)}
          className={labelClass}
          data-testid={`dep-row-${node.name}`}
        >
          {node.displayName}
        </button>
      </span>
      <span className="text-text tabular-nums shrink-0">
        {formatNodeValue(node.value)}
      </span>
    </div>
  );
}

function formatNodeValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    if (Number.isInteger(value) && Math.abs(value) < 1e6)
      return value.toLocaleString('en-US');
    return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Input branch — Phase 4 Session 3
// ---------------------------------------------------------------------------

interface InputBranchProps {
  cell: GridCell;
  schema: ModelViewResponse['schema'];
  inputValues: Record<string, unknown>;
  provenance: Record<string, unknown>;
  conversationId: string | null;
  onViewInConversation?: () => void;
  /** Sandbox edit pathway — see `CellDetailPanelProps.onInputEdit` JSDoc. */
  onInputEdit?: (namedRange: string, value: unknown) => void;
  /** Billing read-only gate — disables editor + short-circuits handleSubmit. */
  readOnly?: boolean;
}

interface ProvenanceFields {
  source?: string;
  confidence?: number;
  timestamp?: string;
  benchmarkRange?: { min?: number; max?: number };
}

function readProvenanceFields(raw: unknown): ProvenanceFields {
  if (raw === null || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  return {
    source: typeof r.source === 'string' ? r.source : undefined,
    confidence: typeof r.confidence === 'number' ? r.confidence : undefined,
    timestamp: typeof r.timestamp === 'string' ? r.timestamp : undefined,
    benchmarkRange:
      typeof r.benchmarkRange === 'object' && r.benchmarkRange !== null
        ? (r.benchmarkRange as { min?: number; max?: number })
        : undefined,
  };
}

interface ValidRangeSubset {
  min?: number;
  max?: number;
}

function readValidRange(raw: unknown): ValidRangeSubset | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const min = typeof r.min === 'number' ? r.min : undefined;
  const max = typeof r.max === 'number' ? r.max : undefined;
  if (min === undefined && max === undefined) return null;
  return { min, max };
}

function formatRangeValue(value: number, dataType: string | undefined): string {
  if (dataType === 'percentage') {
    return `${(value * 100).toFixed(2)}%`;
  }
  if (
    dataType === 'currency' ||
    dataType === 'currency_per_sf' ||
    dataType === 'currency_per_unit_year'
  ) {
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

/**
 * Map a server `ApiError` from `POST /model-edit` to an inline error message
 * that adapts the wording per the 4 known error codes (spec §3 + Phase 2
 * controller). Returns `null` when the error code is unrecognized — caller
 * surfaces a generic fallback.
 */
function mapEditErrorMessage(error: ApiError, draft: string): string {
  const detail =
    typeof error.details?.message === 'string'
      ? (error.details.message as string)
      : error.message;
  switch (error.code) {
    case 'VALIDATION_FAILED':
      return detail || 'Value out of range — try again.';
    case 'NORMALIZE_FAILED':
      return `Couldn't parse "${draft}". ${detail}`.trim();
    case 'UNKNOWN_INPUT':
      return 'This input is no longer recognized by the active model.';
    case 'EMPTY_EDIT':
      return 'No change to commit.';
    case 'ENGINE_ERROR':
      return 'Server error — try again.';
    default:
      return detail || `Edit failed: ${error.message}`;
  }
}

function InputBranch({
  cell,
  schema,
  inputValues,
  provenance,
  conversationId,
  onViewInConversation,
  onInputEdit,
  readOnly = false,
}: InputBranchProps) {
  const namedRange = cell.namedRange ?? null;
  const def: ResolvedInputDef | null = useMemo(
    () => (namedRange ? findInputDefinition(namedRange, schema) : null),
    [namedRange, schema]
  );
  const currentValue = namedRange ? inputValues[namedRange] ?? null : null;
  const provenanceFields = useMemo(
    () => readProvenanceFields(namedRange ? provenance[namedRange] : undefined),
    [namedRange, provenance]
  );
  const validRange = useMemo(
    () =>
      readValidRange(
        // `validRange` is on the InputDefinition (schema.inputs[i]) per
        // `validRangeSchema`. It's not on the ResolvedInputDef so re-read
        // from the raw schema entry.
        namedRange
          ? (schema.inputs as Array<Record<string, unknown>>).find(
              (i) => i.name === namedRange
            )?.validRange
          : null
      ),
    [namedRange, schema.inputs]
  );
  const downstream = useMemo(
    () => (namedRange ? getDownstreamNamedRanges(namedRange, schema) : []),
    [namedRange, schema]
  );

  const mutation = useModelEdit();
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ systemMessageId: string | null } | null>(
    null
  );
  // Keep the latest draft so the error message can quote it back to the user
  // ("Couldn't parse '...'") without re-deriving from editor state.
  const [lastDraft, setLastDraft] = useState<string>('');

  // Clear toast + error whenever the selected cell changes (defensive — the
  // `key` prop on `<InputBranch>` upstream would also remount, but cell-level
  // state cleanup belongs here regardless).
  useEffect(() => {
    setError(null);
    setToast(null);
  }, [namedRange]);

  const handleSubmit = (value: unknown) => {
    if (!namedRange) return;
    // Billing read-only short-circuit (defense-in-depth alongside the disabled
    // input + Save button). At 100% usage the editor is disabled in the UI,
    // but a programmatic submit (via a stale handler reference or a future
    // keyboard shortcut) must also no-op here so it can't bypass the gate.
    // The server-side 402 PAYMENT_REQUIRED on `/model-edit` is the
    // authoritative gate — this just keeps the UI honest.
    if (readOnly) return;
    setError(null);
    setLastDraft(typeof value === 'string' ? value : String(value));

    // Sandbox path (`external-sharing` Phase 5 Session 5.4) — when
    // `onInputEdit` is set, route the edit through the parent's
    // sandbox-overrides hook INSTEAD of the auth-mode mutation. The
    // sandbox flow has no `conversationId` (it operates on a frozen
    // share snapshot, not a live conversation), no DB write, and no
    // Realtime echo — the parent's overlay merge handles re-render.
    if (onInputEdit) {
      onInputEdit(namedRange, value);
      return;
    }

    // Auth-mode path — original commit pathway via `useModelEdit`.
    if (!conversationId) return;
    mutation.mutate(
      { conversationId, namedRange, value },
      {
        onSuccess: (data: ModelEditResponse) => {
          // SINGLE-SOURCE-OF-TRUTH INVARIANT (spec §8): do NOT update
          // `inputValues` / `cellValues` here, do NOT invalidate the
          // modelView query. The Realtime echo via Session 4's bridge ref
          // delivers the canonical update to the grid + this panel's
          // `currentValue` ~150-300ms later.
          setToast({ systemMessageId: data.systemMessageId });
        },
        onError: (err) => {
          setError(mapEditErrorMessage(err, lastDraft || String(value)));
        },
      }
    );
  };

  /**
   * ADR 0027 (Session 4) — location-resolved handler for the grid path.
   * Called by `<InputCellEditor onLocationResolved>` when the user selects
   * a Google Places prediction on a dataType:'location' input. Carries the
   * full `{placeId, formattedAddress, sessionToken}` payload so the server
   * hook can fire `resolvePropertyLocation` and write full enrichment.
   *
   * The sessionToken is the native `google.maps.places.AutocompleteSessionToken`
   * object; JSON.stringify it so the Zod schema (z.string()) accepts it.
   *
   * Not wired through `onInputEdit` (sandbox path) — sandbox recompute is a
   * read-only operation that doesn't need location enrichment. Falls through
   * to a no-op when `conversationId` is absent or the sandbox path is active.
   */
  const handleLocationResolved = (v: LocationResolvedValue) => {
    if (!namedRange || readOnly || onInputEdit || !conversationId) return;
    setError(null);
    setLastDraft(v.formattedAddress);
    const serialisedToken =
      v.sessionToken !== undefined ? JSON.stringify(v.sessionToken) : undefined;
    mutation.mutate(
      {
        conversationId,
        namedRange,
        value: v.formattedAddress,
        placeId: v.placeId,
        sessionToken: serialisedToken,
      },
      {
        onSuccess: (data: ModelEditResponse) => {
          setToast({ systemMessageId: data.systemMessageId });
        },
        onError: (err) => {
          setError(mapEditErrorMessage(err, v.formattedAddress));
        },
      }
    );
  };

  if (!namedRange || !def) {
    return (
      <div className="p-4">
        <div className="text-xs font-mono text-text-muted leading-relaxed">
          This input cell isn't fully described by the model schema. Edit via
          the chat thread instead.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3" data-testid="cell-detail-input-branch">
      {/* Header */}
      <div>
        <div className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase">
          Input
        </div>
        <div
          className="text-sm font-mono text-text-bright mt-0.5"
          data-testid="input-header"
        >
          {def.displayName ?? def.name}
        </div>
      </div>

      {toast && (
        <EditSyncedToast
          systemMessageId={toast.systemMessageId}
          onDismiss={() => setToast(null)}
          onViewInConversation={() => {
            setToast(null);
            onViewInConversation?.();
          }}
        />
      )}

      <InputCellEditor
        def={def}
        currentValue={currentValue}
        pending={mutation.isPending}
        readOnly={readOnly}
        onSubmit={handleSubmit}
        onLocationResolved={handleLocationResolved}
        error={error}
        conversationId={conversationId}
      />

      {validRange && (
        <div
          className="text-xs font-mono text-text-dim leading-snug"
          data-testid="input-valid-range"
        >
          Must be between{' '}
          {validRange.min !== undefined
            ? formatRangeValue(validRange.min, def.dataType)
            : '—'}
          {' and '}
          {validRange.max !== undefined
            ? formatRangeValue(validRange.max, def.dataType)
            : '—'}
        </div>
      )}

      {/* Provenance */}
      {(provenanceFields.source || provenanceFields.timestamp) && (
        <div
          className="border-t border-panel-border pt-2 space-y-1"
          data-testid="input-provenance"
        >
          {provenanceFields.source && (
            <ProvRow label="Source" value={provenanceFields.source} />
          )}
          {typeof provenanceFields.confidence === 'number' && (
            <ProvRow
              label="Confidence"
              value={`${Math.round(provenanceFields.confidence * 100)}%`}
            />
          )}
          {provenanceFields.timestamp && (
            <ProvRow
              label="Set"
              value={formatProvenanceTimestamp(provenanceFields.timestamp)}
            />
          )}
        </div>
      )}

      {/* Benchmark (rare on canonical model — shown only when populated). */}
      {provenanceFields.benchmarkRange && (
        <div
          className="text-xs font-mono text-text-muted leading-snug"
          data-testid="input-benchmark"
        >
          Benchmark range:{' '}
          {provenanceFields.benchmarkRange.min !== undefined
            ? formatRangeValue(
                provenanceFields.benchmarkRange.min,
                def.dataType
              )
            : '—'}
          {' to '}
          {provenanceFields.benchmarkRange.max !== undefined
            ? formatRangeValue(
                provenanceFields.benchmarkRange.max,
                def.dataType
              )
            : '—'}
        </div>
      )}

      {/* Downstream impact list — names only (spec §3, prevValue=null for downstream). */}
      {downstream.length > 0 && (
        <div
          className="border-t border-panel-border pt-2"
          data-testid="input-downstream"
        >
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px] mb-1">
            Affects ({downstream.length})
          </div>
          <ul className="space-y-0.5">
            {downstream.map((d) => (
              <li
                key={d.name}
                className="text-xs font-mono text-text-muted truncate"
                data-testid={`input-downstream-${d.name}`}
              >
                {d.displayName}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ProvRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs font-mono">
      <span className="text-text-dim uppercase tracking-[1px]">{label}</span>
      <span className="text-text">{value}</span>
    </div>
  );
}
